import type { Vehicle } from 'fuel-benchmark-contracts';
import { useMemo } from 'react';
import { Checkbox } from '../../components/ui/Checkbox';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { StatusText } from '../../components/ui/StatusText';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { powertrainTone, vehicleTitle } from '../vehicles/vehicle-display';
import { MAX_COMPARE_VEHICLES } from './compare-selection';

export interface VehicleSelectTableProps {
  vehicles: readonly Vehicle[];
  selectedIds: readonly string[];
  onToggle: (vehicleId: string) => void;
  isLoading: boolean;
}

/**
 * Step A of the compare screen (plan §7): the vehicle catalogue as a checkbox table. Clicking anywhere on
 * a row toggles it, and once four vehicles are picked the rest go quiet rather than disappearing — the
 * comparison and the report only have room for four columns.
 */
export const VehicleSelectTable = ({ vehicles, selectedIds, onToggle, isLoading }: VehicleSelectTableProps) => {
  const { t } = useI18nTranslation();

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const isFull = selected.size >= MAX_COMPARE_VEHICLES;

  const columns: DataTableColumns<Vehicle> = [
    {
      id: 'select',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const isSelected = selected.has(row.original.id);

        return (
          <Checkbox
            id={`compare-vehicle-${row.original.id}`}
            checked={isSelected}
            disabled={!isSelected && isFull}
            aria-label={t('compare.vehicles.select', { code: row.original.code })}
            onChange={() => onToggle(row.original.id)}
            onClick={(event) => event.stopPropagation()}
          />
        );
      },
    },
    {
      accessorKey: 'code',
      header: t('vehicles.columns.code'),
      cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.code}</span>,
    },
    {
      id: 'vehicle',
      accessorFn: (vehicle) => vehicleTitle(vehicle),
      header: t('vehicles.columns.vehicle'),
      cell: ({ row }) => (
        <InfoCell
          title={vehicleTitle(row.original)}
          subline={row.original.trim}
        />
      ),
    },
    {
      accessorKey: 'segmentName',
      header: t('vehicles.columns.segment'),
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.segmentName}</span>,
    },
    {
      accessorKey: 'powertrainType',
      header: t('vehicles.columns.powertrain'),
      cell: ({ row }) => (
        <StatusText
          label={t(`enums.powertrain.${row.original.powertrainType}`)}
          tone={powertrainTone(row.original.powertrainType)}
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={[...vehicles]}
      isLoading={isLoading}
      emptyMessage={t('compare.vehicles.empty')}
      onRowClick={(vehicle) => {
        if (selected.has(vehicle.id) || !isFull) {
          onToggle(vehicle.id);
        }
      }}
    />
  );
};
